import { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Box,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Select,
  MenuItem,
  Checkbox,
  IconButton,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { ncdAPI } from '../../../../services/ncd.api';
import { getLocationFacilityDetails } from '../../utils/constants';

const AddTreatmentForm = ({ individualId, htnScreenId, onSubmit, treatmentHistory = [], loadingTreatment = false, onCancel }) => {
  const loggedData = useMemo(() => {
    return JSON.parse(
      localStorage.getItem("loggedinUserdetails") ||
      localStorage.getItem("NCD_loggedinUserdetails") ||
      "{}"
    );
  }, []);

  const facilityDetails = useMemo(() => {
    return getLocationFacilityDetails(loggedData);
  }, [loggedData]);

  const referFurtherToFacility = facilityDetails.referFurtherToFacility;
  const facilityId = facilityDetails.facilityId;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    medication: false,
    followup: false
  });
  const [open, setOpen] = useState(false);
  const [loadedMedicines, setLoadedMedicines] = useState([]);
  const [loadedPrescriptions, setLoadedPrescriptions] = useState([]);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [medicineSearchText, setMedicineSearchText] = useState('');


  // Fetch medicine suggestions dynamically
  useEffect(() => {
    const fetchMedicineOptions = async () => {
      if (medicineSearchText && medicineSearchText.length >= 1) {
        try {
          const response = await ncdAPI.getInitialAssessmentMedicines(medicineSearchText);
          if (response && response.data) {
            setMedicineOptions(response.data);
          }
        } catch (error) {
          console.error('Error fetching medicine options:', error);
        }
      } else {
        setMedicineOptions([]);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchMedicineOptions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [medicineSearchText]);

  // Fetch existing current medications/prescriptions on mount
  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const response = await ncdAPI.getInitialAssessmentMedications(individualId);
        if (response && response.data) {
          const medicines = (response.data.medicines || []).filter(med =>
            (med.conditn || '').toLowerCase().includes('hyper')
          );
          const prescriptions = (response.data.medicinesPrescribed || []).filter(med =>
            (med.conditn || '').toLowerCase().includes('hyper')
          );

          setLoadedMedicines(medicines.map(med => ({
            date: med.date || '',
            condition: med.conditn || '',
            medicine: med.name || '',
            frequency: med.freq || '',
            duration: med.duratn || '',
            durationUnit: 'Days',
            quantity: String(med.quan || ''),
            instruction: med.spInst || '',
            medicineSourceType: med.medicineSourceType,
            isLoaded: true,
            isDeletable: true
          })));

          setLoadedPrescriptions(prescriptions.map(med => ({
            date: med.date || '',
            condition: med.conditn || '',
            medicine: med.name || '',
            frequency: med.freq || '',
            duration: med.duratn || '',
            durationUnit: 'Days',
            quantity: String(med.quan || ''),
            instruction: med.spInst || '',
            medicineSourceType: med.medicineSourceType,
            lastUpdatedBy: med.lastUpdatedBy,
            lastUpdatedFacility: med.lastUpdatedFacility,
            isLoaded: true,
            isDeletable: false
          })));
        }
      } catch (error) {
        console.error('Error fetching medications:', error);
      }
    };

    if (individualId) {
      fetchMedications();
    }
  }, [individualId]);

  // Calculate next visit date based on days/weeks/months
  const calculateNextVisitDate = (days, unit, baseDate = dayjs()) => {
    if (!days || isNaN(days) || !baseDate) return null;

    const numDays = parseInt(days);
    switch (unit) {
      case 'Weeks':
        return baseDate.add(numDays * 7, 'day');
      case 'Months':
        return baseDate.add(numDays, 'month');
      default: // Days
        return baseDate.add(numDays, 'day');
    }
  };

  const handleSubmitForm = async (values) => {
    try {
      setIsSubmitting(true);

      const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails"));

      const facility = loggedData?.facilityType;

      // Calculate followUpDuration: "days;weeks;months"
      let followUpDuration = "28;0;0";
      if (values.followUpDays) {
        if (values.followUpUnit === 'Weeks') {
          followUpDuration = `0;${values.followUpDays};0`;
        } else if (values.followUpUnit === 'Months') {
          followUpDuration = `0;0;${values.followUpDays}`;
        } else {
          followUpDuration = `${values.followUpDays};0;0`;
        }
      }

      // Format medications
      const selMeds = values.medications
        .filter(med => med.medicine)
        .map(med => ({
          conditn: "Hypertension",
          name: med.medicine,
          prscrbdStrngth: null,
          freq: med.frequency === "OD" ? "Once a day" : med.frequency === "BD" ? "Twice a day" : med.frequency === "TDS" ? "Thrice a day" : med.frequency === "QDS" ? "Four times a day" : med.frequency,
          quan: Number(med.quantity) || null,
          dur: med.duration,
          durType: null,
          spInst: med.instruction
        }));

      // Refer details
      const referToFaciMap = {
        PHC: 3200,
        CHC: 3300,
        DH: 3400,
        TCC: 3500
      };

      const referPayload = values.refer ? {
        refDate: values.date.format("DD-MM-YYYY"),
        refSpeclty: loggedData?.facilityTypeId || facilityId,
        refToFaci: referToFaciMap[values.referTo] || Number(values.referTo) || 3500,
        refReason: values.remarks,
        privtHosptalCare: true,
        refFromFaci: facilityId || loggedData?.facilityTypeId || 3200,
        comms: values.remarks
      } : null;

      const sys = Number(values.systolic);
      const diast = Number(values.diastolic);
      const bloodPressureControl = (sys < 140 && diast < 90) ? "Controlled" : "Uncontrolled";

      const payload = {
        programId: 1,
        examinationId: htnScreenId,
        examination: {
          isVitalsEdited: true,
          exam: {
            assessDate: values.date.format("DD-MM-YYYY"),
            sys: sys,
            diast: diast,
            heartRate: values.heartRate ? Number(values.heartRate) : null
          }
        },
        diagnosis: {
          assessmentDate: values.date.format("DD-MM-YYYY"),
          selectedDiagnosis: values.diagnosed === "hypertension" ? "CONFIRMED_STAGE_1" : "NAD",
          prescriptionAvailable: true,
          bloodPressureControl: bloodPressureControl,
          isPatientAlreadyOnTreatment: values.knownCase === "yes"
        },
        treatment: {
          treat: {
            facility: facility,
            treatDate: values.date.format("DD-MM-YYYY"),
            reviewDate: values.date.format("DD-MM-YYYY"),
            sys: sys,
            diast: diast,
            heartRate: values.heartRate ? Number(values.heartRate) : null,
            selMeds: values.diagnosed === "hypertension" ? selMeds : [],
            removeCurrMedication: false,
            treatDateWithTimeStamp: null,
            conDn: values.counselling,
            dgns: {
              assessDate: values.date.format("DD-MM-YYYY"),
              diagRresult: values.diagnosed === "hypertension" ? "CONFIRMED_STAGE_1" : "NAD"
            },
            docfolUp: values.diagnosed === "hypertension" ? {
              followUpDueDate: values.followUpDate ? values.followUpDate.format("DD-MM-YYYY") : null,
              followUpDuration: followUpDuration,
              followUpReason: "Review",
              remarks: values.remarks || null
            } : null,
            isTreatmentOnProtocol: false,
            diagnosedHypertensionStage: values.diagnosed === "hypertension" ? 1 : null,
            bloodPressureControl: bloodPressureControl,
            refer: values.diagnosed === "hypertension" ? referPayload : null
          }
        }
      };

      const response = await ncdAPI.submitHTNDBFollowup(500, individualId, payload);

      if (response && response.success) {
        toast.success(response.message || 'Hypertension treatment saved successfully!', {
          className: 'custom-toast-success',
        });
        onSubmit?.(values);
      } else {
        toast.error(response?.message || 'Failed to save hypertension treatment', {
          className: 'custom-toast-error',
        });
      }
    } catch (err) {
      console.error("Error submitting HTN followup:", err);
      toast.error(err.response?.data?.message || 'Something went wrong while saving treatment', {
        className: 'custom-toast-error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDiagHistoryShow = !loadingTreatment && (
    treatmentHistory.length === 0 || treatmentHistory.every(item => (item.diagnosis || '').trim() === 'NAD')
  );

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      knownCase: isDiagHistoryShow
        ? Yup.string().required('Mark as a known case is required')
        : Yup.string().notRequired(),
      systolic: Yup.number()
        .required('Systolic BP is required')
        .integer('Must be an integer')
        .positive('Must be positive')
        .min(50, 'Systolic pressure is too low')
        .max(300, 'Systolic pressure is too high'),
      diastolic: Yup.number()
        .required('Diastolic BP is required')
        .integer('Must be an integer')
        .positive('Must be positive')
        .min(30, 'Diastolic pressure is too low')
        .max(200, 'Diastolic pressure is too high'),
      heartRate: Yup.number()
        .nullable()
        .notRequired()
        .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
        .integer('Must be an integer')
        .positive('Must be positive')
        .min(30, 'Heart rate is too low')
        .max(250, 'Heart rate is too high'),
      counselling: Yup.boolean().when('diagnosed', {
        is: 'hypertension',
        then: () => Yup.boolean().oneOf([true], 'Counselling is mandatory'),
        otherwise: () => Yup.boolean().notRequired()
      }),
      refer: Yup.boolean(),
      referTo: Yup.string().when('refer', {
        is: true,
        then: () => Yup.string().required('Refer facility is required'),
        otherwise: () => Yup.string().notRequired(),
      }),
      remarks: Yup.string().when('refer', {
        is: true,
        then: () => Yup.string().required('Remarks are required'),
        otherwise: () => Yup.string().notRequired(),
      }),
      medications: Yup.array().of(
        Yup.object().shape({
          medicine: Yup.string(),
          frequency: Yup.string().when('medicine', {
            is: (val) => val && val.length > 0,
            then: () => Yup.string().required('Frequency is required'),
            otherwise: () => Yup.string().notRequired(),
          }),
          duration: Yup.number().when('medicine', {
            is: (val) => val && val.length > 0,
            then: () => Yup.number().required('Duration is required').positive('Must be positive').integer('Must be an integer'),
            otherwise: () => Yup.number().notRequired(),
          }),
          quantity: Yup.number().when('medicine', {
            is: (val) => val && val.length > 0,
            then: () => Yup.number().required('Quantity is required').positive('Must be positive').integer('Must be an integer'),
            otherwise: () => Yup.number().notRequired(),
          }),
        })
      )
    });
  }, [isDiagHistoryShow]);

  const formik = useFormik({
    initialValues: {
      date: dayjs(),
      knownCase: '',
      systolic: '',
      diastolic: '',
      heartRate: '',
      diagnosed: isDiagHistoryShow ? '' : 'hypertension',
      medications: [{ medicine: '', frequency: '', duration: '', quantity: '', instruction: 'After Food' }],
      counselling: false,
      followUpDays: '28',
      followUpUnit: 'Days',
      followUpDate: dayjs().add(28, 'day'),
      refer: false,
      referTo: '',
      remarks: ''
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmitForm
  });

  useEffect(() => {
    if (!loadingTreatment) {
      if (!isDiagHistoryShow) {
        if (formik.values.diagnosed !== 'hypertension') {
          formik.setFieldValue('diagnosed', 'hypertension');
        }
      } else {
        if (!formik.values.knownCase && formik.values.diagnosed === 'hypertension') {
          formik.setFieldValue('diagnosed', '');
        }
      }
    }
  }, [isDiagHistoryShow, loadingTreatment]);

  useEffect(() => {
    if (formik.values.diagnosed === 'hypertension') {
      setExpandedSections(prev => ({ ...prev, medication: true }));
    } else {
      setExpandedSections({ medication: false, followup: false });
    }
  }, [formik.values.diagnosed]);

  const isVitalsDisabled = isDiagHistoryShow && !formik.values.knownCase;
  const isDiagnosedDisabled = !isDiagHistoryShow || !formik.values.knownCase || formik.values.knownCase === 'yes';
  const isCounsellingDisabled = !formik.values.diagnosed;
  const isMedicationFollowUpDisabled = formik.values.diagnosed !== 'hypertension';

  // Handle follow-up days change
  const handleFollowUpDaysChange = (value) => {
    formik.setFieldValue('followUpDays', value);
    const nextDate = calculateNextVisitDate(value, formik.values.followUpUnit, formik.values.date);
    formik.setFieldValue('followUpDate', nextDate);
  };

  // Handle follow-up unit change
  const handleFollowUpUnitChange = (value) => {
    formik.setFieldValue('followUpUnit', value);
    const nextDate = calculateNextVisitDate(formik.values.followUpDays, value, formik.values.date);
    formik.setFieldValue('followUpDate', nextDate);
  };

  // Handle follow-up date picker change
  const handleFollowUpDateChange = (newDate) => {
    if (!newDate) {
      formik.setFieldValue('followUpDate', null);
      formik.setFieldValue('followUpDays', '');
      return;
    }

    const daysDiff = newDate.diff(formik.values.date, 'day');
    formik.setFieldValue('followUpDate', newDate);
    formik.setFieldValue('followUpDays', daysDiff.toString());
    formik.setFieldValue('followUpUnit', 'Days');
  };

  const handleMedicationChange = (index, field, value) => {
    const newMedications = [...formik.values.medications];
    newMedications[index][field] = value;
    formik.setFieldValue('medications', newMedications);
  };

  // Auto-calculate quantity based on frequency and duration
  useEffect(() => {
    const frequencyMap = {
      'OD': 1,
      'BD': 2,
      'TDS': 3,
      'QDS': 4,
      'Once a day': 1,
      'Twice a day': 2,
      'Thrice a day': 3,
      'Four times a day': 4
    };

    if (formik.values.medications) {
      let updated = false;
      const newMedications = formik.values.medications.map(med => {
        if (med.frequency && med.duration) {
          const freqValue = frequencyMap[med.frequency] || 0;
          const durationValue = parseInt(med.duration) || 0;
          const calculatedQuantity = freqValue * durationValue;

          if (calculatedQuantity > 0 && med.quantity !== String(calculatedQuantity)) {
            updated = true;
            return { ...med, quantity: String(calculatedQuantity) };
          }
        } else if (med.quantity !== '') {
          updated = true;
          return { ...med, quantity: '' };
        }
        return med;
      });

      if (updated) {
        formik.setFieldValue('medications', newMedications);
      }
    }
  }, [formik.values.medications, formik.setFieldValue]);

  const addMedication = () => {
    formik.setFieldValue('medications', [
      ...formik.values.medications,
      { medicine: '', frequency: '', duration: '', quantity: '', instruction: 'After Food' }
    ]);
  };

  const removeMedication = (index) => {
    formik.setFieldValue('medications', formik.values.medications.filter((_, i) => i !== index));
  };

  const toggleSection = (section) => {
    if (isMedicationFollowUpDisabled) return;
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="add-treatment-form">
        <Box className="form-header">
          <DatePicker
            disabled={isVitalsDisabled}
            value={formik.values.date}
            maxDate={dayjs()}
            onChange={(newValue) => {
              formik.setFieldValue('date', newValue);
              const nextDate = calculateNextVisitDate(formik.values.followUpDays, formik.values.followUpUnit, newValue);
              formik.setFieldValue('followUpDate', nextDate);
            }}
            format="DD-MM-YYYY"
            slotProps={{
              textField: {
                size: 'small',
                className: 'date-picker'
              }
            }}
          />
        </Box>

        {/* Diagnosis History Section */}
        {isDiagHistoryShow && (
          <Box className="form-section">
            <h3>Diagnosis History<span className="required">*</span></h3>
            <Box className="diagnosis-history">
              <label>Mark as a known case</label>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="knownCase"
                  value={formik.values.knownCase}
                  onChange={(e) => {
                    const val = e.target.value;
                    formik.setFieldValue('knownCase', val);
                    if (val === 'yes') {
                      formik.setFieldValue('diagnosed', 'hypertension');
                    } else if (val === 'no') {
                      formik.setFieldValue('diagnosed', 'no-abnormality');
                    }
                  }}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Blood Pressure Section */}
        <Box className="form-section">
          <Box className="section-header">
            <Box className="icon-wrapper">
              <span className="section-icon">🩺</span>
            </Box>
            <h3>Blood Pressure</h3>
          </Box>
          <Box className="vitals-inputs">
            <Box className="vital-group">
              <label>Systolic / Diastolic* (mmHg)</label>
              <Box className="bp-inputs">
                <TextField
                  disabled={isVitalsDisabled}
                  placeholder="___"
                  name="systolic"
                  value={formik.values.systolic}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.systolic && Boolean(formik.errors.systolic)}
                  size="small"
                />
                <span>/</span>
                <TextField
                  disabled={isVitalsDisabled}
                  placeholder="___"
                  name="diastolic"
                  value={formik.values.diastolic}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.diastolic && Boolean(formik.errors.diastolic)}
                  size="small"
                />
              </Box>
              {((formik.touched.systolic && formik.errors.systolic) || (formik.touched.diastolic && formik.errors.diastolic)) && (
                <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                  {formik.errors.systolic || formik.errors.diastolic}
                </Box>
              )}
            </Box>
            <Box className="vital-group">
              <label>Heart Rate (bpm)</label>
              <TextField
                disabled={isVitalsDisabled}
                name="heartRate"
                value={formik.values.heartRate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.heartRate && Boolean(formik.errors.heartRate)}
                helperText={formik.touched.heartRate && formik.errors.heartRate}
                size="small"
                sx={{ display: 'block' }}
              />
            </Box>
          </Box>
        </Box>

        {/* Diagnosed Section */}
        <Box className="form-section">
          <h3>Diagnosed</h3>
          <FormControl component="fieldset" disabled={isDiagnosedDisabled}>
            <RadioGroup
              row
              name="diagnosed"
              value={formik.values.diagnosed}
              onChange={formik.handleChange}
            >
              <FormControlLabel value="no-abnormality" control={<Radio />} label="No abnormality detected" />
              <FormControlLabel value="hypertension" control={<Radio />} label="Hypertension" />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Medication Section */}
        <Box className="form-section collapsible">
          <Box className="section-header clickable" onClick={() => toggleSection('medication')}>
            <h3>Medication</h3>
            <IconButton size="small" disabled={isMedicationFollowUpDisabled}>
              {expandedSections.medication ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          {expandedSections.medication && (
            <Box className="medication-content">
              <Box className="medication-table">
                <Box className="table-header">
                  <span>Medicine</span>
                  <span>Frequency</span>
                  <span>Duration (in days)</span>
                  <span>Quantity</span>
                  <span>Special Instruction</span>
                  <span></span>
                </Box>
                {/* Loaded Medicines (deletable) */}
                {loadedMedicines.map((med, index) => (
                  <Box key={`loaded-${index}`} className="table-row">
                    <TextField value={med.medicine} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <TextField value={med.frequency} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <TextField value={med.duration} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <TextField value={med.quantity} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <TextField value={med.instruction} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <IconButton
                      onClick={() => setLoadedMedicines(prev => prev.filter((_, i) => i !== index))}
                      size="small"
                      color="error"
                      disabled={isMedicationFollowUpDisabled}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}

                {/* Loaded Prescriptions (not deletable) */}
                {loadedPrescriptions.map((med, index) => (
                  <Box key={`prescription-${index}`} className="table-row">
                    <TextField value={med.medicine} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <TextField value={med.frequency} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <TextField value={med.duration} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <TextField value={med.quantity} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <TextField value={med.instruction} disabled size="small" fullWidth sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }} />
                    <Box></Box>
                  </Box>
                ))}

                {formik.values.medications.map((med, index) => (
                  <Box key={index} className="table-row-container" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box className="table-row">
                      <Autocomplete
                        disabled={isMedicationFollowUpDisabled}
                        options={medicineOptions}
                        value={med.medicine || null}
                        onChange={(event, newValue) => {
                          handleMedicationChange(index, 'medicine', newValue || '');
                        }}
                        onInputChange={(event, newInputValue) => {
                          setMedicineSearchText(newInputValue);
                        }}
                        freeSolo
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Search medicine..."
                          />
                        )}
                        sx={{ width: '100%' }}
                      />
                      <Select
                        value={med.frequency}
                        onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                        size="small"
                        disabled={isMedicationFollowUpDisabled || !med.medicine}
                        error={formik.touched.medications?.[index]?.frequency && Boolean(formik.errors.medications?.[index]?.frequency)}
                      >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="OD">OD</MenuItem>
                        <MenuItem value="BD">BD</MenuItem>
                        <MenuItem value="TDS">TDS</MenuItem>
                        <MenuItem value="QDS">QDS</MenuItem>
                      </Select>
                      <TextField
                        value={med.duration}
                        onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                        size="small"
                        placeholder="2"
                        disabled={isMedicationFollowUpDisabled || !med.medicine}
                        error={formik.touched.medications?.[index]?.duration && Boolean(formik.errors.medications?.[index]?.duration)}
                      />
                      <TextField
                        value={med.quantity}
                        size="small"
                        placeholder="4"
                        disabled
                      />
                      <Select
                        value={med.instruction}
                        onChange={(e) => handleMedicationChange(index, 'instruction', e.target.value)}
                        size="small"
                        disabled={isMedicationFollowUpDisabled || !med.medicine}
                      >
                        <MenuItem value="After Food">After Food</MenuItem>
                        <MenuItem value="Before Food">Before Food</MenuItem>
                      </Select>
                      <IconButton
                        onClick={() => removeMedication(index)}
                        size="small"
                        color="error"
                        disabled={isMedicationFollowUpDisabled}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    {formik.touched.medications?.[index] && formik.errors.medications?.[index] && (
                      <Box sx={{ color: 'error.main', fontSize: '0.75rem', ml: 2, mb: 1 }}>
                        {Object.values(formik.errors.medications[index]).join(', ')}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
              <Button
                onClick={addMedication}
                variant="text"
                color="primary"
                className="add-medicine-btn"
                disabled={isMedicationFollowUpDisabled}
              >
                + ADD ANOTHER MEDICINE
              </Button>
            </Box>
          )}
        </Box>

        {/* Counselling Section */}
        <Box className="form-section">
          <Box className="counselling-checkbox">
            <FormControlLabel
              disabled={isCounsellingDisabled}
              control={
                <Checkbox
                  name="counselling"
                  checked={formik.values.counselling}
                  onChange={formik.handleChange}
                  disabled={isCounsellingDisabled}
                />
              }
              label={
                <span>
                  Counselling for prevention control & management (including Obesity) done <span className="required">*</span>
                </span>
              }
            />
            {formik.touched.counselling && formik.errors.counselling && (
              <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                {formik.errors.counselling}
              </Box>
            )}
          </Box>
        </Box>

        {/* Follow-up/Refer Section */}
        <Box className="form-section collapsible">
          <Box className="section-header clickable" onClick={() => toggleSection('followup')}>
            <h3>Follow-up/Refer</h3>
            <IconButton size="small" disabled={isMedicationFollowUpDisabled}>
              {expandedSections.followup ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          {expandedSections.followup && (
            <Box className="followup-content">
              <Box className="followup-row">
                <Box className="followup-group">
                  <label>Follow-up</label>
                  <Box className="followup-inputs">
                    <span>Next visit on:</span>

                    {formik.values.followUpDate && (
                      <span className="next-date" style={{ color: '#666', fontSize: '14px' }}>
                        {formik.values.followUpDate.format('DD-MM-YYYY')}
                      </span>
                    )}

                  </Box>
                  <Box className="followup-duration" style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <TextField
                      value={formik.values.followUpDays}
                      onChange={(e) => handleFollowUpDaysChange(e.target.value)}
                      size="small"
                      className="followup-days"
                      placeholder="28"
                      type="number"
                      disabled={isMedicationFollowUpDisabled}
                    />
                    <Select
                      value={formik.values.followUpUnit}
                      onChange={(e) => handleFollowUpUnitChange(e.target.value)}
                      size="small"
                      sx={{ width: "200px" }}
                      disabled={isMedicationFollowUpDisabled}
                    >
                      <MenuItem value="Days">Days</MenuItem>
                      <MenuItem value="Weeks">Weeks</MenuItem>
                      <MenuItem value="Months">Months</MenuItem>
                    </Select>

                    <IconButton onClick={() => setOpen(true)} sx={{ color: '#1976d2' }} disabled={isMedicationFollowUpDisabled}>
                      <CalendarTodayIcon />
                    </IconButton>

                    <DatePicker
                      open={open}
                      onClose={() => setOpen(false)}
                      value={formik.values.followUpDate}
                      onChange={handleFollowUpDateChange}
                      format="DD-MM-YYYY"
                      minDate={formik.values.date}
                      disabled={isMedicationFollowUpDisabled}
                      slotProps={{
                        textField: {
                          sx: {
                            position: "absolute",
                            opacity: 0,
                            width: 0,
                            height: 0,
                            padding: 0,
                            margin: 0
                          }
                        }
                      }}
                    />

                  </Box>
                </Box>
                <Box className="refer-group">
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="refer"
                        checked={formik.values.refer}
                        onChange={formik.handleChange}
                        disabled={isMedicationFollowUpDisabled}
                      />
                    }
                    label="Refer"
                  />
                  {formik.values.refer && (
                    <Grid container spacing={2} className="refer-details">
                      <Grid size={{ xs: 3 }}>
                        <Select
                          name="referTo"
                          value={formik.values.referTo}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.referTo && Boolean(formik.errors.referTo)}
                          displayEmpty
                          size="small"
                          fullWidth
                          disabled={isMedicationFollowUpDisabled}
                        >
                          <MenuItem value="">Select</MenuItem>
                          {referFurtherToFacility.map((fac) => (
                            <MenuItem key={fac.id} value={fac.name}>
                              {fac.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.referTo && formik.errors.referTo && (
                          <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                            {formik.errors.referTo}
                          </Box>
                        )}
                      </Grid>
                      <Grid size={{ xs: 9 }}>
                        <TextField
                          placeholder="Enter Remarks"
                          name="remarks"
                          value={formik.values.remarks}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                          helperText={formik.touched.remarks && formik.errors.remarks}
                          fullWidth
                          size="small"
                          disabled={isMedicationFollowUpDisabled}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box className="form-actions">
          <Button variant="outlined" onClick={() => setOpenCancelDialog(true)}>
            CANCEL
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={formik.handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'SAVING...' : 'SAVE'}
          </Button>
        </Box>

        <Dialog
          open={openCancelDialog}
          onClose={() => setOpenCancelDialog(false)}
          aria-labelledby="cancel-dialog-title"
          PaperProps={{
            sx: {
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '450px',
              width: '100%',
            }
          }}
        >
          <DialogContent sx={{ p: 0, mb: 3 }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#333', lineHeight: 1.5 }}>
              This page contains unsaved changes. Do you still wish to leave the page?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 0, justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={() => setOpenCancelDialog(false)}
              variant="outlined"
              sx={{
                borderColor: '#3f51b5',
                color: '#000000',
                fontWeight: 'bold',
                minWidth: '80px',
                height: '40px',
                '&:hover': {
                  borderColor: '#303f9f',
                  backgroundColor: 'rgba(63, 81, 181, 0.04)',
                }
              }}
            >
              NO
            </Button>
            <Button
              onClick={() => {
                setOpenCancelDialog(false);
                if (onCancel) {
                  onCancel();
                } else {
                  window.history.back();
                }
              }}
              variant="contained"
              sx={{
                backgroundColor: '#3f51b5',
                color: '#fff',
                fontWeight: 'bold',
                minWidth: '80px',
                height: '40px',
                '&:hover': {
                  backgroundColor: '#303f9f',
                }
              }}
            >
              YES
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AddTreatmentForm;
